import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Warning, ClockClockwise, CheckCircle, XCircle, Upload, IdentificationCard, Crosshair } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import DynamicKycForm from './DynamicKycForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_MB = 5;

const CERT_LABELS = {
  gst: 'GST Certificate', udyam: 'Udyam / MSME Registration', shop_act: 'Shop & Establishment Act',
  msme: 'MSME Certificate', fssai: 'FSSAI License', trade_license: 'Trade License', other: 'Other Govt. Certificate',
};
const ID_LABELS = {
  aadhaar: 'Aadhaar Card', pan: 'PAN Card', voter: 'Voter ID', passport: 'Passport', driving_license: 'Driving License',
};
const PROOF_LABELS = {
  training_cert: 'Professional Training Certificate',
  work_id: 'Work ID Proof',
  social_media: 'Social Media Page (work-related posts)',
};

const STATUS_META = {
  not_required: { icon: ShieldCheck, color: 'text-text-secondary', bg: 'bg-gray-100', label: 'Not Required', tone: 'KYC is only required for Indian phone numbers.' },
  not_submitted: { icon: Warning, color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-300', label: 'Action Required', tone: 'Upload your documents to start accepting orders.' },
  pending_review: { icon: ClockClockwise, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-300', label: 'Under Review', tone: 'Your documents are with our admin team. Typical review: 1-2 business days.' },
  approved: { icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50 border-green-300', label: 'Approved', tone: 'KYC is verified. (Once the joining fee is paid, you can start accepting orders.)' },
  rejected: { icon: XCircle, color: 'text-red-700', bg: 'bg-red-50 border-red-300', label: 'Rejected', tone: 'Please review the reason below and re-upload corrected documents.' },
};

const validateFile = (f, label, allowImages = true, allowPdf = true) => {
  if (!f) return `${label}: please choose a file`;
  if (f.size > MAX_FILE_MB * 1024 * 1024) return `${label}: file must be under ${MAX_FILE_MB} MB`;
  const allowed = [];
  if (allowImages) allowed.push('image/jpeg', 'image/png', 'image/webp');
  if (allowPdf) allowed.push('application/pdf');
  if (!allowed.includes(f.type)) {
    const exts = allowed.map(m => m.split('/')[1].toUpperCase()).join(' / ');
    return `${label}: only ${exts} allowed`;
  }
  return null;
};

/**
 * Vendor / Professional KYC card.
 *
 * Renders a different upload form based on the user's role:
 *   • vendor       → business certificate + contact-person ID
 *   • professional → personal govt ID + profile pic + ONE of:
 *                    (training cert | work ID | social media URL)
 */
const KycCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Vendor form fields
  const [certificateType, setCertificateType] = useState('gst');
  const [certFile, setCertFile] = useState(null);
  const [businessPan, setBusinessPan] = useState('');

  // Shared fields
  const [idType, setIdType] = useState('aadhaar');
  const [idFile, setIdFile] = useState(null);
  const [contactName, setContactName] = useState('');

  // Professional form fields
  const [profilePic, setProfilePic] = useState(null);
  const [proofType, setProofType] = useState('training_cert');
  const [proofFile, setProofFile] = useState(null);
  const [proofUrl, setProofUrl] = useState('');

  const [submitting, setSubmitting] = useState(false);
  // Address + base_city — required for geofence-based customer matching (Feb 2026)
  const [address, setAddress] = useState('');
  const [baseCity, setBaseCity] = useState('');
  const [masterCities, setMasterCities] = useState([]);
  // Exact location captured via "Use my current location" button.
  const [area, setArea] = useState('');
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [locating, setLocating] = useState(false);

  // Reverse-geocodes the user's current GPS coords into address + city + area,
  // then auto-fills the form. Warns if detected city differs from selected.
  const useMyLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Geolocation is not supported on this device');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&accept-language=en`,
          { headers: { 'Accept': 'application/json' } },
        );
        const dat = await r.json();
        const a = dat?.address || {};
        const detected = a.city || a.town || a.village || a.suburb || a.county || a.state_district || a.state;
        const detectedArea = a.suburb || a.neighbourhood || a.village || a.town || a.city_district || '';
        const full = dat?.display_name || '';
        const matchedCity = (masterCities || []).find((c) => c.toLowerCase() === (detected || '').toLowerCase());
        setLat(latitude); setLng(longitude); setArea(detectedArea);
        if (full) setAddress(full.slice(0, 500));
        if (matchedCity) {
          if (baseCity && baseCity !== matchedCity) {
            toast.warning(`Detected ${matchedCity} but you'd picked ${baseCity} — updating base city.`);
          }
          setBaseCity(matchedCity);
          toast.success(`Location captured · ${matchedCity}${detectedArea ? ' · ' + detectedArea : ''}`);
        } else if (detected) {
          toast.warning(`Detected city "${detected}" is not on our service list. Pick the closest one.`);
        } else {
          toast.error("Couldn't detect your city — fill it manually.");
        }
      } catch {
        toast.error('Reverse-geocoding failed — please fill the address manually.');
      }
      setLocating(false);
    }, (err) => {
      const msg = err.code === err.PERMISSION_DENIED
        ? 'Permission denied — please fill the address manually'
        : 'Could not detect location';
      toast.error(msg);
      setLocating(false);
    }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 });
  };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${BACKEND_URL}/api/kyc/status`, { withCredentials: true });
      setData(d);
      if (d.contact_name) setContactName(d.contact_name);
      if (d.business_pan) setBusinessPan(d.business_pan);
      if (d.address) setAddress(d.address);
      if (d.base_city) setBaseCity(d.base_city);
      if (d.area) setArea(d.area);
      if (typeof d.lat === 'number') setLat(d.lat);
      if (typeof d.lng === 'number') setLng(d.lng);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load KYC status');
    }
    setLoading(false);
  };

  // Load master city list (admin-curated) once on mount.
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/service-cities`)
      .then(r => setMasterCities(r.data?.cities || []))
      .catch(() => setMasterCities([]));
  }, []);

  useEffect(() => { fetchStatus(); }, []);

  const submitVendor = async () => {
    if (!contactName.trim()) return toast.error('Contact person name is required');
    if (!address.trim()) return toast.error('Full address is required');
    if (!baseCity) return toast.error('Pick your base city');
    const ce = validateFile(certFile, 'Certificate'); if (ce) return toast.error(ce);
    const ie = validateFile(idFile, 'ID'); if (ie) return toast.error(ie);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('certificate_type', certificateType);
      fd.append('certificate_file', certFile);
      fd.append('id_type', idType);
      fd.append('id_file', idFile);
      fd.append('contact_name', contactName.trim());
      fd.append('address', address.trim());
      fd.append('base_city', baseCity);
      if (area) fd.append('area', area);
      if (typeof lat === 'number') fd.append('lat', String(lat));
      if (typeof lng === 'number') fd.append('lng', String(lng));
      if (businessPan.trim()) fd.append('business_pan', businessPan.trim());
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/kyc/submit`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      toast.success('KYC submitted — admin will review shortly');
      setCertFile(null); setIdFile(null);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'KYC submission failed');
    }
    setSubmitting(false);
  };

  const submitPro = async () => {
    if (!contactName.trim()) return toast.error('Display name is required');
    if (!address.trim()) return toast.error('Full address is required');
    if (!baseCity) return toast.error('Pick your base city');
    const ie = validateFile(idFile, 'ID'); if (ie) return toast.error(ie);
    const pe = validateFile(profilePic, 'Profile picture', true, false); if (pe) return toast.error(pe);
    const waiver = data?.category === 'server_waiter';
    if (!waiver) {
      if (proofType === 'social_media') {
        const u = (proofUrl || '').trim();
        if (!u || !/^https?:\/\//i.test(u)) return toast.error('Enter a valid http(s) URL for the social media page');
      } else {
        const fe = validateFile(proofFile, 'Work proof'); if (fe) return toast.error(fe);
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('id_type', idType);
      fd.append('id_file', idFile);
      fd.append('contact_name', contactName.trim());
      fd.append('profile_pic', profilePic);
      fd.append('address', address.trim());
      fd.append('base_city', baseCity);
      if (area) fd.append('area', area);
      if (typeof lat === 'number') fd.append('lat', String(lat));
      if (typeof lng === 'number') fd.append('lng', String(lng));
      if (!waiver) {
        fd.append('proof_type', proofType);
        if (proofType === 'social_media') {
          fd.append('proof_url', proofUrl.trim());
        } else {
          fd.append('proof_file', proofFile);
        }
      }
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/kyc/submit-professional`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      toast.success('KYC submitted — admin will review shortly');
      setIdFile(null); setProfilePic(null); setProofFile(null); setProofUrl('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'KYC submission failed');
    }
    setSubmitting(false);
  };

  if (loading || !data) {
    return (
      <Card className="bg-white border-[#E8E1D5]" data-testid="kyc-card-loading">
        <CardContent className="py-6 text-sm text-text-secondary text-center">Loading KYC status...</CardContent>
      </Card>
    );
  }

  const isPro = data.role === 'professional';
  const isWaiter = isPro && data.category === 'server_waiter';
  const meta = STATUS_META[data.kyc_status] || STATUS_META.not_submitted;
  const Icon = meta.icon;
  const canSubmit = data.is_indian_phone && (data.kyc_status === 'not_submitted' || data.kyc_status === 'rejected');

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="kyc-card">
      <CardHeader className="border-b border-[#E8E1D5]">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ShieldCheck size={22} className="text-brand-primary" /> KYC Verification
        </CardTitle>
        <CardDescription>
          {isPro
            ? (isWaiter
                ? 'Upload your personal government ID and a profile picture. Waiters are exempt from the work-proof step.'
                : 'Upload your personal government ID, profile picture, and ONE work-proof (training certificate, work ID, or a social media page with work-related posts).')
            : 'Upload one government business certificate plus one government ID of your contact person.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-5">
        <div className={`p-3 border-2 rounded ${meta.bg}`} data-testid={`kyc-status-${data.kyc_status}`}>
          <div className="flex items-start gap-2">
            <Icon size={20} className={meta.color} weight="duotone" />
            <div className="flex-1">
              <div className={`text-sm font-bold ${meta.color}`}>{meta.label}</div>
              <div className="text-xs text-text-secondary mt-0.5">{meta.tone}</div>
              {data.kyc_status === 'rejected' && data.rejection_reason && (
                <div className="mt-2 text-xs text-red-800 bg-white/60 border border-red-200 p-2 rounded">
                  <strong>Reason:</strong> {data.rejection_reason}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Preview already-submitted docs (read-only) */}
        {(data.kyc_status === 'pending_review' || data.kyc_status === 'approved') && data.documents && (
          <div className="space-y-2 text-xs">
            {data.documents.certificate && (
              <div className="flex justify-between border border-[#E8E1D5] p-2 rounded">
                <span><strong>{CERT_LABELS[data.documents.certificate.type] || data.documents.certificate.type}</strong></span>
                <a href={`${BACKEND_URL}${data.documents.certificate.url}`} target="_blank" rel="noreferrer" className="text-brand-primary hover:underline" data-testid="kyc-cert-link">View</a>
              </div>
            )}
            {data.documents.id && (
              <div className="flex justify-between border border-[#E8E1D5] p-2 rounded">
                <span><strong>{ID_LABELS[data.documents.id.type] || data.documents.id.type}</strong></span>
                <a href={`${BACKEND_URL}${data.documents.id.url}`} target="_blank" rel="noreferrer" className="text-brand-primary hover:underline" data-testid="kyc-id-link">View</a>
              </div>
            )}
            {data.documents.profile_pic && (
              <div className="flex justify-between border border-[#E8E1D5] p-2 rounded">
                <span><strong>Profile picture</strong></span>
                <a href={`${BACKEND_URL}${data.documents.profile_pic.url}`} target="_blank" rel="noreferrer" className="text-brand-primary hover:underline" data-testid="kyc-profile-link">View</a>
              </div>
            )}
            {data.documents.pro_proof && (
              <div className="flex justify-between border border-[#E8E1D5] p-2 rounded">
                <span><strong>{PROOF_LABELS[data.documents.pro_proof.type] || data.documents.pro_proof.type}</strong></span>
                {data.documents.pro_proof.url ? (
                  <a href={`${BACKEND_URL}${data.documents.pro_proof.url}`} target="_blank" rel="noreferrer" className="text-brand-primary hover:underline" data-testid="kyc-proof-link">View</a>
                ) : data.documents.pro_proof.external_url ? (
                  <a href={data.documents.pro_proof.external_url} target="_blank" rel="noreferrer" className="text-brand-primary hover:underline" data-testid="kyc-proof-link">Open page</a>
                ) : null}
              </div>
            )}
            {data.contact_name && (
              <div className="border border-[#E8E1D5] p-2 rounded">{isPro ? 'Display' : 'Contact'}: <strong>{data.contact_name}</strong>{data.business_pan ? <> · PAN: <strong>{data.business_pan}</strong></> : null}</div>
            )}
          </div>
        )}

        {!data.is_indian_phone && (
          <p className="text-xs text-text-secondary italic">
            KYC is currently required only for Indian phone numbers. Your account is auto-approved for international onboarding (joining fee still applies).
          </p>
        )}

        {canSubmit && (
          <div className="border-t border-[#E8E1D5] pt-4">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-3">Upload Documents</h3>
            <DynamicKycForm onSubmitted={fetchStatus} />
          </div>
        )}

        {/* Legacy hardcoded forms below are kept temporarily for fallback; */}
        {/* they're gated to never render now that DynamicKycForm covers both roles. */}
        {false && canSubmit && !isPro && (
          <form onSubmit={(e) => { e.preventDefault(); submitVendor(); }} className="space-y-3 border-t border-[#E8E1D5] pt-4">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary">Upload Documents</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Certificate Type</Label>
                <select value={certificateType} onChange={(e) => setCertificateType(e.target.value)}
                  className="w-full h-9 border border-[#E8E1D5] rounded-none px-2 text-sm" data-testid="kyc-cert-type">
                  {Object.entries(CERT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Certificate File <span className="text-red-500">*</span></Label>
                <Input type="file" accept="image/*,.pdf" onChange={(e) => setCertFile(e.target.files?.[0] || null)}
                  className="rounded-none border-[#E8E1D5] h-9 text-xs" data-testid="kyc-cert-file" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Contact Person ID Type</Label>
                <select value={idType} onChange={(e) => setIdType(e.target.value)}
                  className="w-full h-9 border border-[#E8E1D5] rounded-none px-2 text-sm" data-testid="kyc-id-type">
                  {Object.entries(ID_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">ID File <span className="text-red-500">*</span></Label>
                <Input type="file" accept="image/*,.pdf" onChange={(e) => setIdFile(e.target.files?.[0] || null)}
                  className="rounded-none border-[#E8E1D5] h-9 text-xs" data-testid="kyc-id-file" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Contact Person Name <span className="text-red-500">*</span></Label>
                <Input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)}
                  className="rounded-none border-[#E8E1D5] h-9" data-testid="kyc-contact-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Business PAN <span className="text-text-secondary font-normal">(optional)</span></Label>
                <Input type="text" value={businessPan} onChange={(e) => setBusinessPan(e.target.value.toUpperCase())}
                  className="rounded-none border-[#E8E1D5] h-9 uppercase" maxLength={10} data-testid="kyc-business-pan" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs font-bold text-text-secondary">Full Address <span className="text-red-500">*</span></Label>
                  <button type="button" onClick={useMyLocation} disabled={locating}
                    className="px-2 py-1 border border-[#E8E1D5] hover:bg-brand-accent/10 transition-colors flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-brand-primary disabled:opacity-60 rounded-sm"
                    data-testid="kyc-use-location-vendor">
                    <Crosshair size={11} weight={locating ? 'fill' : 'regular'} />
                    {locating ? 'Detecting…' : 'Use my current location'}
                  </button>
                </div>
                <textarea value={address} onChange={(e) => setAddress(e.target.value)}
                  rows={2} maxLength={500}
                  className="w-full rounded-none border border-[#E8E1D5] p-2 text-sm" data-testid="kyc-address"
                  placeholder="House / shop number, street, area, landmark" />
                {(typeof lat === 'number' && typeof lng === 'number') && (
                  <p className="text-[10px] text-emerald-700 font-medium" data-testid="kyc-geo-captured">
                    <Crosshair size={11} weight="fill" className="inline mr-0.5" />
                    Exact location saved · {lat.toFixed(5)}, {lng.toFixed(5)}{area ? ` · ${area}` : ''}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Base City <span className="text-red-500">*</span></Label>
                <select value={baseCity} onChange={(e) => setBaseCity(e.target.value)}
                  className="w-full h-9 border border-[#E8E1D5] rounded-none px-2 text-sm bg-white" data-testid="kyc-base-city">
                  <option value="">— Pick your city —</option>
                  {masterCities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-[10px] text-text-secondary italic">Customers in this city get matched to you automatically. Extras you add later (no transport allowance).</p>
              </div>
            </div>
            <p className="text-[10px] text-text-secondary italic">
              Files: JPG / PNG / WEBP / PDF, max {MAX_FILE_MB} MB each. Make sure all corners are visible and the text is readable.
            </p>
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}
                className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none flex items-center gap-1"
                data-testid="kyc-submit-btn">
                <Upload size={14} /> {submitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </div>
          </form>
        )}

        {false && canSubmit && isPro && (
          <form onSubmit={(e) => { e.preventDefault(); submitPro(); }} className="space-y-3 border-t border-[#E8E1D5] pt-4">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary">Upload Documents</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Personal Govt ID Type <span className="text-red-500">*</span></Label>
                <select value={idType} onChange={(e) => setIdType(e.target.value)}
                  className="w-full h-9 border border-[#E8E1D5] rounded-none px-2 text-sm" data-testid="kyc-id-type">
                  {Object.entries(ID_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">ID File <span className="text-red-500">*</span></Label>
                <Input type="file" accept="image/*,.pdf" onChange={(e) => setIdFile(e.target.files?.[0] || null)}
                  className="rounded-none border-[#E8E1D5] h-9 text-xs" data-testid="kyc-id-file" />
                <p className="text-[10px] text-text-secondary italic">Aadhaar / PAN / Voter / Passport / Driving Licence. Not a company registration certificate.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Display Name <span className="text-red-500">*</span></Label>
                <Input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)}
                  className="rounded-none border-[#E8E1D5] h-9" data-testid="kyc-contact-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary flex items-center gap-1">
                  <IdentificationCard size={12} /> Profile Picture <span className="text-red-500">*</span>
                </Label>
                <Input type="file" accept="image/*" onChange={(e) => setProfilePic(e.target.files?.[0] || null)}
                  className="rounded-none border-[#E8E1D5] h-9 text-xs" data-testid="kyc-profile-pic" />
                <p className="text-[10px] text-text-secondary italic">JPG / PNG / WEBP, max {MAX_FILE_MB} MB. Shown on your customer-facing card.</p>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs font-bold text-text-secondary">Full Address <span className="text-red-500">*</span></Label>
                  <button type="button" onClick={useMyLocation} disabled={locating}
                    className="px-2 py-1 border border-[#E8E1D5] hover:bg-brand-accent/10 transition-colors flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-brand-primary disabled:opacity-60 rounded-sm"
                    data-testid="kyc-use-location-pro">
                    <Crosshair size={11} weight={locating ? 'fill' : 'regular'} />
                    {locating ? 'Detecting…' : 'Use my current location'}
                  </button>
                </div>
                <textarea value={address} onChange={(e) => setAddress(e.target.value)}
                  rows={2} maxLength={500}
                  className="w-full rounded-none border border-[#E8E1D5] p-2 text-sm" data-testid="kyc-address"
                  placeholder="Home / shop number, street, area, landmark" />
                {(typeof lat === 'number' && typeof lng === 'number') && (
                  <p className="text-[10px] text-emerald-700 font-medium" data-testid="kyc-geo-captured-pro">
                    <Crosshair size={11} weight="fill" className="inline mr-0.5" />
                    Exact location saved · {lat.toFixed(5)}, {lng.toFixed(5)}{area ? ` · ${area}` : ''}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-text-secondary">Base City <span className="text-red-500">*</span></Label>
                <select value={baseCity} onChange={(e) => setBaseCity(e.target.value)}
                  className="w-full h-9 border border-[#E8E1D5] rounded-none px-2 text-sm bg-white" data-testid="kyc-base-city">
                  <option value="">— Pick your city —</option>
                  {masterCities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-[10px] text-text-secondary italic">Customers in this city get matched to you automatically. Extras you add later (no transport allowance).</p>
              </div>
            </div>
            <div className="border-2 border-brand-primary/30 rounded p-3 bg-brand-accent/5 space-y-3" style={isWaiter ? { display: 'none' } : undefined} data-testid="kyc-proof-block">
              <div className="text-xs tracking-[0.18em] uppercase font-bold text-brand-primary">Work Proof — pick one</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {Object.entries(PROOF_LABELS).map(([k, v]) => (
                  <button key={k} type="button"
                    onClick={() => { setProofType(k); setProofFile(null); setProofUrl(''); }}
                    className={`p-2 border-2 text-left text-xs transition-all ${proofType === k ? 'border-brand-primary bg-white' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
                    data-testid={`kyc-proof-type-${k}`}>
                    <div className={`font-bold ${proofType === k ? 'text-brand-primary' : ''}`}>{v.split(' (')[0]}</div>
                  </button>
                ))}
              </div>
              {proofType === 'social_media' ? (
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-text-secondary">Social Media URL <span className="text-red-500">*</span></Label>
                  <Input type="url" value={proofUrl} onChange={(e) => setProofUrl(e.target.value)}
                    placeholder="https://instagram.com/your-work-page"
                    className="rounded-none border-[#E8E1D5] h-9" data-testid="kyc-proof-url" />
                  <p className="text-[10px] text-text-secondary italic">Public profile only. Page must contain work-related posts (portfolio shots, performance clips, etc.).</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-text-secondary">{PROOF_LABELS[proofType]} File <span className="text-red-500">*</span></Label>
                  <Input type="file" accept="image/*,.pdf" onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                    className="rounded-none border-[#E8E1D5] h-9 text-xs" data-testid="kyc-proof-file" />
                  <p className="text-[10px] text-text-secondary italic">JPG / PNG / WEBP / PDF, max {MAX_FILE_MB} MB.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}
                className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none flex items-center gap-1"
                data-testid="kyc-submit-btn">
                <Upload size={14} /> {submitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default KycCard;
